import React, { useState, useMemo, useEffect } from 'react'
import { FormControl, InputLabel, MenuItem, Select, type SelectChangeEvent } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { PREFIX_LUT_STORAGE_KEY, US_PREFIX_LUT_STORAGE_KEY, useGlobalMessageHandler, usePrefixLuts, useUsPrefixLuts } from 'components'
import { SessionRegion, useSessionContext } from 'session'
import { PrefixCreationDialog } from './prefix-creation-dialog'
import { type Prefix } from 'services'

interface PrefixSelectProps {
  value: number | null
  onChange: (event: SelectChangeEvent<number>, child: React.ReactNode) => void
  disabled?: boolean
  allowCreation?: boolean
}

export const PrefixSelect: React.FC<PrefixSelectProps> = ({
  value,
  onChange,
  disabled = false,
  allowCreation = false
}) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const { showErrorMessage } = useGlobalMessageHandler()
  const { sessionData } = useSessionContext()
  const isUs = sessionData.region === SessionRegion.US
  const usPrefixLuts = useUsPrefixLuts()
  const internationalPrefixLuts = usePrefixLuts()
  const { lutValues: prefixLutValues, setLutValues, loading: lutLoading } = isUs ? usPrefixLuts : internationalPrefixLuts
  const localStorageKey = isUs ? US_PREFIX_LUT_STORAGE_KEY : PREFIX_LUT_STORAGE_KEY

  const displayValue = value === 0 ? '' : value

  const allPrefixes = useMemo(
    () => [...prefixLutValues.prefixes].sort((a: Prefix, b: Prefix) => a.lutOrder - b.lutOrder),
    [prefixLutValues.prefixes]
  )

  useEffect(() => {
    if (value != null && value !== 0 && !lutLoading && prefixLutValues.currentTime != null) {
      const selectedPrefixExists = prefixLutValues.prefixes.some(prefix => prefix.id === value)
      if (!selectedPrefixExists) {
        const lutTime = new Date(prefixLutValues.currentTime + 'Z')
        const isRecentUpdate = new Date().getTime() - lutTime.getTime() < 15000

        if (isRecentUpdate) {
          showErrorMessage('Invalid prefix selected. Please reload the page and contact dev team if the issue persists.')
        } else {
          localStorage.removeItem(localStorageKey)
          location.reload()
        }
      }
    }
  }, [value, lutLoading, prefixLutValues.prefixes, prefixLutValues.currentTime])

  const handleAddNewClick = (): void => {
    setDialogOpen(true)
  }

  const handlePrefixCreated = (newPrefix: Prefix): void => {
    setLutValues({
      ...prefixLutValues,
      prefixes: [...prefixLutValues.prefixes, newPrefix]
    })

    onChange({
      target: { value: newPrefix.id, name: 'prefixId' }
    } as unknown as React.ChangeEvent<HTMLInputElement>, null)
  }

  const handleSelectChange = (event: SelectChangeEvent<number>, child: React.ReactNode): void => {
    const selectedValue = event.target.value as number
    if (selectedValue === -1) {
      handleAddNewClick()
      return
    }
    onChange(event, child)
  }

  return (
    <>
      <FormControl fullWidth variant="outlined">
        <InputLabel id="prefix_id_label">Prefix</InputLabel>
        <Select
          id="prefix_id"
          name="prefixId"
          label="Prefix"
          labelId="prefix_id_label"
          value={displayValue ?? ''}
          onChange={handleSelectChange}
          disabled={disabled}
        >
          {allPrefixes.map((option: Prefix) => (
            <MenuItem key={option.id} value={option.id}>
              {option.id === 0 ? <em>None</em> : option.code}
            </MenuItem>
          ))}
          {allowCreation && (
              <MenuItem value={-1}>
                <AddIcon fontSize="small" sx={{ mr: 1 }} />
                Add New Prefix
              </MenuItem>
          )}
        </Select>
      </FormControl>

      {allowCreation && (
        <PrefixCreationDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false)
          }}
          onPrefixCreated={handlePrefixCreated}
        />
      )}
    </>
  )
}
